import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import * as api from '../../services/api';
import styles from './AdminDashboard.module.css';

export default function AdminDashboard() {
    const { t } = useLanguage();
    const [stats, setStats] = useState(null);
    const [recentPending, setRecentPending] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const [statsRes, pendingRes] = await Promise.all([
                api.getAdminStats(),
                api.getPendingConfigurations({ limit: 5 }),
            ]);

            if (statsRes.success) {
                setStats(statsRes.data);
            }
            if (pendingRes.success) {
                setRecentPending(pendingRes.data || []);
            }
        } catch (err) {
            console.error('Failed to load dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.loading}>
                {t('common.loading') || 'Loading...'}
            </div>
        );
    }

    const statCards = [
        {
            label: t('admin.stats.pendingReview') || 'Pending Review',
            value: stats?.pending_count || 0,
            icon: '⏳',
            color: '#ffc107',
            link: '/admin/review',
        },
        {
            label: t('admin.stats.approved') || 'Approved',
            value: stats?.approved_count || 0,
            icon: '✅',
            color: '#4caf50',
        },
        {
            label: t('admin.stats.rejected') || 'Rejected',
            value: stats?.rejected_count || 0,
            icon: '❌',
            color: '#f44336',
        },
        {
            label: t('admin.stats.totalUsers') || 'Total Users',
            value: stats?.users_count || 0,
            icon: '👥',
            color: '#2196f3',
        },
        {
            label: t('admin.stats.officialConfigs') || 'Official Configs',
            value: stats?.official_count || 0,
            icon: '⭐',
            color: '#ff6600',
            link: '/admin/official',
        },
        {
            label: t('admin.stats.totalLikes') || 'Total Likes',
            value: stats?.total_likes || 0,
            icon: '❤️',
            color: '#e91e63',
        },
    ];

    return (
        <div className={styles.dashboard}>
            <h1 className={styles.title}>
                {t('admin.dashboard.title') || 'Admin Dashboard'}
            </h1>

            {/* Stats Grid */}
            <div className={styles.statsGrid}>
                {statCards.map((stat, index) => (
                    <div key={index} className={styles.statCard}>
                        <div className={styles.statIcon} style={{ background: stat.color }}>
                            {stat.icon}
                        </div>
                        <div className={styles.statInfo}>
                            <span className={styles.statValue}>{stat.value}</span>
                            <span className={styles.statLabel}>{stat.label}</span>
                        </div>
                        {stat.link && (
                            <Link to={stat.link} className={styles.statLink}>→</Link>
                        )}
                    </div>
                ))}
            </div>

            {/* Recent Pending */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2>{t('admin.dashboard.recentPending') || 'Recent Pending'}</h2>
                    <Link to="/admin/review" className={styles.viewAllLink}>
                        {t('admin.dashboard.viewAll') || 'View All'} →
                    </Link>
                </div>

                {recentPending.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>{t('admin.dashboard.noPending') || 'No pending submissions'}</p>
                    </div>
                ) : (
                    <div className={styles.pendingList}>
                        {recentPending.map(config => (
                            <Link
                                to={`/admin/review?id=${config.id}`}
                                key={config.id}
                                className={styles.pendingItem}
                            >
                                <div className={styles.pendingImage}>
                                    {config.cover_image_url ? (
                                        <img src={config.cover_image_url} alt="" />
                                    ) : (
                                        <div className={styles.noImage}>{t('common.noImage')}</div>
                                    )}
                                </div>
                                <div className={styles.pendingInfo}>
                                    <h4>{config.title}</h4>
                                    <p>
                                        {t('admin.dashboard.by')} {config.user?.nickname || t('common.anonymous')}
                                    </p>
                                    <span className={styles.pendingTime}>
                                        {new Date(config.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <span className={styles.reviewBtn}>
                                    {t('admin.dashboard.review') || 'Review'}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className={styles.section}>
                <h2>{t('admin.dashboard.quickActions') || 'Quick Actions'}</h2>
                <div className={styles.quickActions}>
                    <Link to="/admin/review" className={styles.actionBtn}>
                        <span className={styles.actionIcon}>📝</span>
                        <span>{t('admin.actions.reviewQueue') || 'Review Queue'}</span>
                    </Link>
                    <Link to="/admin/official" className={styles.actionBtn}>
                        <span className={styles.actionIcon}>⭐</span>
                        <span>{t('admin.actions.addOfficial') || 'Add Official Config'}</span>
                    </Link>
                    <Link to="/gallery" className={styles.actionBtn}>
                        <span className={styles.actionIcon}>🖼️</span>
                        <span>{t('admin.actions.viewGallery') || 'View Gallery'}</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}
